import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Bot, MessageSquare, Phone, Database, FileText, TrendingUp, Users, Building2, Brain, Zap, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const AISolutions = () => {
  const aiCapabilities = [
    'Automate repetitive business processes',
    'Answer customer and employee questions instantly',
    'Search and understand large document repositories',
    'Extract information from PDFs, invoices and documents',
    'Automate finance and accounting workflows',
    'Generate reports and management insights',
    'Qualify and follow up with leads',
    'Automate HR processes',
    'Provide 24×7 customer support',
    'Handle conversations through WhatsApp',
    'Handle inbound and outbound voice calls',
    'Connect AI with ERP and CRM systems',
    'Build intelligent internal knowledge assistants',
    'Analyse business data and identify patterns',
    'Trigger actions automatically based on business rules',
    'Reduce manual intervention across business processes'
  ];

  const implementationSteps = [
    { number: '1', title: 'Discover', description: 'We understand your existing processes, systems, data and business objectives.' },
    { number: '2', title: 'Identify', description: 'We identify processes where AI can generate measurable business value.' },
    { number: '3', title: 'Design', description: 'We design the AI architecture, integrations, workflows and user experience.' },
    { number: '4', title: 'Build', description: 'We develop the AI assistant, agent, knowledge system or automation.' },
    { number: '5', title: 'Integrate', description: 'We connect AI with your ERP, CRM, databases, documents, communication channels and business applications.' },
    { number: '6', title: 'Test', description: 'We test accuracy, security, workflows, exceptions and human escalation.' },
    { number: '7', title: 'Deploy', description: 'We deploy the solution into your business environment.' },
    { number: '8', title: 'Improve', description: 'AI systems continuously evolve as your business processes, knowledge and requirements change.' }
  ];

  const businessFunctions = [
    { function: 'Finance', opportunities: 'Invoice processing, reporting, reconciliation, collections' },
    { function: 'Sales', opportunities: 'Lead qualification, follow-ups, proposals, forecasting' },
    { function: 'Customer Service', opportunities: 'AI chatbot, voice agents, ticket automation' },
    { function: 'HR', opportunities: 'Employee assistant, recruitment, onboarding' },
    { function: 'Procurement', opportunities: 'Supplier analysis, PO processing, price analysis' },
    { function: 'Operations', opportunities: 'Workflow automation, exception management' },
    { function: 'Management', opportunities: 'AI reporting, business insights, decision support' },
    { function: 'IT', opportunities: 'Knowledge assistants, support automation' },
    { function: 'Legal', opportunities: 'Contract analysis, document search, knowledge assistants' },
    { function: 'Manufacturing', opportunities: 'Production insights, inventory and procurement intelligence' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1677442136019-21780ecad995" 
            alt="Artificial Intelligence" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-purple-900/95 via-blue-900/90 to-indigo-900/95"></div>
        </div>

        <div className="absolute inset-0 z-0 opacity-20">
          <div className="absolute top-20 left-20 w-72 h-72 bg-purple-400 rounded-full filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-400 rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="container mx-auto px-4 z-10 relative">
          <div className="max-w-5xl mx-auto text-center text-white">
            <div className="inline-block mb-6">
              <span className="px-4 py-2 bg-purple-500/30 backdrop-blur-sm rounded-full text-sm font-semibold border border-purple-300/50">
                Artificial Intelligence & Business Automation
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Transform Your Business with <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-300 to-blue-300">AI That Actually Works</span>
            </h1>
            <p className="text-xl md:text-2xl mb-4 text-gray-200">
              From AI Assistants to Autonomous Business Agents
            </p>
            <p className="text-base md:text-lg mb-8 text-gray-300 max-w-4xl mx-auto">
              Artificial Intelligence is moving beyond chatbots and content generation. At Gemini High Tech Systems, we help businesses integrate AI directly into their day-to-day operations — connecting AI with business data, ERP systems, CRM platforms, documents, websites, WhatsApp, voice communication and internal knowledge.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-purple-900 hover:bg-gray-100 text-lg px-8 py-6 shadow-xl">
                  Talk to an AI Expert <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/ai-products">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-purple-900 text-lg px-8 py-6">
                  Explore AI Solutions
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What AI Can Do */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">What Can AI Do for Your Business?</h2>
              <p className="text-lg text-gray-600 max-w-4xl mx-auto">
                Most businesses already have enormous amounts of information — but that information is distributed across ERP systems, CRM platforms, Excel files, PDFs, emails, websites, databases and messaging platforms. AI can bring this information together and turn it into action.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {aiCapabilities.map((capability, index) => (
                <div key={index} className="flex items-start space-x-3 p-4 rounded-lg hover:bg-gray-50 transition-colors">
                  <CheckCircle2 className="h-6 w-6 text-purple-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">{capability}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AI Formula */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">Build Your AI-Powered Business</h2>
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
              <div className="text-xl md:text-2xl font-semibold text-gray-800 leading-relaxed">
                <span className="text-purple-600">AI</span> + <span className="text-blue-600">Your Data</span> + <span className="text-indigo-600">Your Applications</span> + <span className="text-violet-600">Your Processes</span>
                <div className="text-4xl my-4">=</div>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600 text-3xl md:text-4xl">Intelligent Business Automation</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Functions */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">AI Transformation for Different Business Functions</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Discover how AI can revolutionize every department in your organization
            </p>
          </div>
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6">
              {businessFunctions.map((item, index) => (
                <Card key={index} className="border-2 border-gray-100 hover:border-purple-200 hover:shadow-lg transition-all">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{item.function}</h3>
                    <p className="text-gray-600">{item.opportunities}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Implementation Approach */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-purple-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our AI Implementation Approach</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              From Business Problem to Production AI — We don't start with the technology. We start with the business problem.
            </p>
          </div>
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {implementationSteps.map((step, index) => (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 bg-white">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl font-bold text-white">{step.number}</span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                    <p className="text-sm text-gray-600">{step.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Gemini */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Why Gemini High Tech Systems?</h2>
              <p className="text-xl text-purple-600 font-semibold mb-8">Business Understanding + Technology Expertise</p>
            </div>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 mb-4">
                AI implementation is not only a technology exercise. A successful AI project requires an understanding of business processes, data, systems and organisational workflows.
              </p>
              <p className="text-gray-700 mb-4">
                Gemini High Tech Systems combines technology capabilities with deep experience across Finance & Accounting, ERP, CRM, Business Process Automation, Digital Transformation, Data & Analytics, AI & Generative AI, Software Development, and Systems Integration.
              </p>
              <p className="text-gray-700 mb-8">
                This enables us to look at AI from both sides: <strong>What can technology do?</strong> and <strong>Where will technology actually create business value?</strong>
              </p>
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-8 mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">AI Doesn't Have to Replace Your Existing Systems</h3>
                <p className="text-gray-700 mb-4">We Make Your Existing Systems Smarter.</p>
                <p className="text-gray-600">
                  You don't necessarily need to replace your ERP. You don't necessarily need to replace your CRM. You don't need to move everything to a new platform. We can build an intelligent layer around your existing technology ecosystem.
                </p>
              </div>
              <div className="text-center">
                <div className="inline-block bg-white rounded-xl shadow-lg p-8">
                  <p className="text-sm font-semibold text-gray-500 mb-2">Your Existing Systems</p>
                  <p className="text-gray-700 mb-4">ERP + CRM + Excel + Documents + Databases + Website + WhatsApp + Email</p>
                  <div className="text-purple-600 text-2xl mb-4">↓</div>
                  <p className="text-sm font-semibold text-purple-600 mb-2">Gemini AI Layer</p>
                  <p className="text-gray-700 mb-4">AI Agents + RAG + Automation + Analytics + Voice + Integrations</p>
                  <div className="text-blue-600 text-2xl mb-4">↓</div>
                  <p className="text-sm font-semibold text-blue-600 mb-2">Business Outcomes</p>
                  <p className="text-gray-700">Faster Decisions • Lower Manual Effort • Better Customer Experience • Higher Productivity • Reduced Errors • 24×7 Availability</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full filter blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Start Your AI Journey</h2>
          <p className="text-xl mb-4 max-w-3xl mx-auto text-purple-100">
            You don't need to transform your entire organisation overnight.
          </p>
          <p className="text-lg mb-8 max-w-2xl mx-auto text-purple-100">
            Start with one high-value process. Identify the bottleneck. Automate it. Measure the result. Then scale.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-purple-600 hover:bg-gray-100 text-lg px-8 py-6 shadow-xl">
                Schedule an AI Discovery Session <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/ai-products">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-purple-600 text-lg px-8 py-6">
                View AI Products
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AISolutions;